import { BooleanInput } from "@angular/cdk/coercion";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { TranslocoPipe } from "@ngneat/transloco";
import { AuthService } from "app/core/auth/auth.service";
import { SessionManagerService } from "app/core/auth/session-manager.service";
import { SessionEntry } from "app/core/auth/session-manager.types";
import { UserService } from "app/core/user/user.service";
import { User } from "app/core/user/user.types";
import { AppConfigService } from "app/services/app-config.service";
import { ClientViewService } from "app/services/client-view.service";
import { Subject, combineLatest, map, takeUntil } from "rxjs";

@Component({
    selector: "user",
    templateUrl: "./user.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: "user",
    standalone: true,
    imports: [
        MatButtonModule,
        MatMenuModule,
        NgIf,
        NgFor,
        MatIconModule,
        NgClass,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        TranslocoPipe,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    sessions: SessionEntry[] = [];
    otherSessions: SessionEntry[] = [];
    activeUserId: number | null = null;
    switchingTo: number | null = null;

    /** Vista de cliente (D-SET32): se ofrece a super admin y solo en pantallas que la soportan. */
    clientViewAvailable = false;
    clientViewOn = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _authService: AuthService,
        private _sessionManager: SessionManagerService,
        private _appConfig: AppConfigService,
        private _clientView: ClientViewService
    ) {}

    ngOnInit(): void {
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
            this.user = user;
            this._changeDetectorRef.markForCheck();
        });

        this._sessionManager.sessions$.pipe(takeUntil(this._unsubscribeAll)).subscribe((sessions: SessionEntry[]) => {
            this.sessions = sessions;
            this.activeUserId = this._sessionManager.getActiveUserId();
            this.otherSessions = sessions.filter((s) => s.userId !== this.activeUserId);
            this._changeDetectorRef.markForCheck();
        });

        combineLatest([
            this._appConfig.config.pipe(map((config) => config?.role === "super-admin")),
            this._clientView.supported$,
            this._clientView.active$,
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(([superAdmin, supported, active]) => {
                this.clientViewAvailable = superAdmin && supported;
                this.clientViewOn = active;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /** Mirar la pantalla como el cliente, o volver a la vista interna (D-SET32). */
    setClientView(on: boolean): void {
        this._clientView.setRequested(on);
    }

    updateUserStatus(status: string): void {
        if (!this.user) {
            return;
        }
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    switchAccount(session: SessionEntry): void {
        const token = this._sessionManager.getTokenForAccount(session.userId);
        if (!token) {
            this._sessionManager.removeSession(session.userId);
            this._changeDetectorRef.markForCheck();
            return;
        }

        this.switchingTo = session.userId;
        this._changeDetectorRef.markForCheck();

        this._authService.validateTokens([token]).subscribe({
            next: (response) => {
                const result = response.results[token];
                if (result?.valid) {
                    const fromUserId = this.activeUserId;
                    this._sessionManager.switchTo(session.userId);

                    if (fromUserId) {
                        this._authService.logAccountSwitch(fromUserId).subscribe();
                    }

                    location.reload();
                } else {
                    this._sessionManager.removeSession(session.userId);
                    this.switchingTo = null;
                    this._changeDetectorRef.markForCheck();
                }
            },
            error: () => {
                this._sessionManager.removeSession(session.userId);
                this.switchingTo = null;
                this._changeDetectorRef.markForCheck();
            },
        });
    }

    removeAccount(session: SessionEntry, event: Event): void {
        event.stopPropagation();
        this._authService.signOut(session.userId).subscribe(() => {
            this._changeDetectorRef.markForCheck();
        });
    }

    addAccount(): void {
        this._router.navigate(["/sign-in"], {
            queryParams: { addAccount: "true" },
        });
    }

    signOut(): void {
        this._router.navigate(["/sign-out"]);
    }

    signOutAll(): void {
        this._router.navigate(["/sign-out"], {
            queryParams: { all: "true" },
        });
    }

    navigateToProfile(): void {
        this._router.navigate(["/profile"]);
    }

    navigateToProfileSettings(): void {
        this._router.navigate(["/profile-settings"]);
    }

    navigateToHelpCenter(): void {
        this._router.navigate(["/tickets"]);
    }

    getInitials(name: string): string {
        if (!name) return "?";
        return name
            .split(" ")
            .map((w) => w[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
    }
}
